import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../../components/Loader";
import { crmHeaders } from "../../utils/crmApi";
import "../../styles/sms-inbox.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const POLL_MS = 30000;

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    return d.toLocaleString();
}

export default function ReceivedSms() {
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [selected, setSelected] = useState(null);
    const [tab, setTab] = useState("inbound");
    const [search, setSearch] = useState("");

    const loadMessages = useCallback(() => {
        const direction = tab === "sent" ? "outbound" : "inbound";
        return axios
            .get(`${BASE_URL}/sms/messages?direction=${direction}`, { headers: crmHeaders() })
            .then((res) => setMessages(res.data))
            .catch((err) => console.error(err));
    }, [tab]);

    useEffect(() => {
        loadMessages().finally(() => setLoading(false));
    }, [loadMessages]);

    useEffect(() => {
        const timer = setInterval(loadMessages, POLL_MS);
        return () => clearInterval(timer);
    }, [loadMessages]);

    const openMessage = (msg) => {
        setSelected(msg);
        if (tab === "inbound" && !msg.Is_Read) {
            axios
                .put(`${BASE_URL}/sms/messages/${msg.Id}/read`, {}, { headers: crmHeaders() })
                .then(() => {
                    setMessages((prev) =>
                        prev.map((m) => (m.Id === msg.Id ? { ...m, Is_Read: 1 } : m))
                    );
                })
                .catch(() => {});
        }
    };

    const filtered = messages.filter((m) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            (m.From_Phone || "").toLowerCase().includes(q) ||
            (m.To_Phone || "").toLowerCase().includes(q) ||
            (m.Body || "").toLowerCase().includes(q) ||
            (m.Client_Name || "").toLowerCase().includes(q)
        );
    });

    const replyToNumber = tab === "inbound" ? selected?.From_Phone : selected?.To_Phone;

    if (loading) return <Loader />;

    return (
        <div className="container-fluid sms-inbox-page">
            <ToastContainer />
            <div className="crm-page-header px-3 pt-3">
                <div>
                    <h1 className="crm-page-title">SMS Inbox</h1>
                    <p className="crm-page-subtitle">Receive and view sent messages on one platform</p>
                </div>
                <Link to="/sms/send" className="btn btn-primary">
                    <i className="bi bi-send me-1"></i> Send SMS
                </Link>
            </div>

            <div className="sms-inbox-layout">
                <aside className="sms-inbox-sidebar">
                    <div className="sms-inbox-tabs">
                        <button
                            type="button"
                            className={tab === "inbound" ? "active" : ""}
                            onClick={() => {
                                setTab("inbound");
                                setSelected(null);
                            }}
                        >
                            Received
                        </button>
                        <button
                            type="button"
                            className={tab === "sent" ? "active" : ""}
                            onClick={() => {
                                setTab("sent");
                                setSelected(null);
                            }}
                        >
                            Sent
                        </button>
                    </div>
                    <input
                        type="search"
                        className="form-control form-control-sm mb-2"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="sms-inbox-list">
                        {filtered.map((msg) => (
                            <button
                                type="button"
                                key={msg.Id}
                                className={`sms-inbox-item ${selected?.Id === msg.Id ? "active" : ""} ${!msg.Is_Read && tab === "inbound" ? "unread" : ""}`}
                                onClick={() => openMessage(msg)}
                            >
                                <div className="sms-inbox-item-top">
                                    <strong>
                                        {tab === "inbound" ? msg.From_Phone : msg.To_Phone}
                                    </strong>
                                    <span>{formatDate(msg.Created_At)}</span>
                                </div>
                                <div className="sms-inbox-item-preview">{msg.Body}</div>
                            </button>
                        ))}
                        {filtered.length === 0 && (
                            <p className="text-muted p-3 mb-0">No messages yet.</p>
                        )}
                    </div>
                </aside>

                <main className="sms-inbox-detail">
                    {selected ? (
                        <>
                            <div className="sms-inbox-detail-header">
                                <div>
                                    <h5>{tab === "inbound" ? "From" : "To"}: {replyToNumber}</h5>
                                    <small className="text-muted">{formatDate(selected.Created_At)}</small>
                                    {selected.Sent_By_User && (
                                        <div>
                                            <small className="text-muted">Sent by: {selected.Sent_By_User}</small>
                                        </div>
                                    )}
                                </div>
                                {tab === "inbound" && replyToNumber && (
                                    <Link
                                        to="/sms/send"
                                        state={{ phone: replyToNumber, name: selected.Client_Name }}
                                        className="btn btn-sm btn-outline-primary"
                                    >
                                        Reply via Send SMS
                                    </Link>
                                )}
                            </div>
                            <div className="sms-inbox-detail-body">{selected.Body}</div>
                            {selected.Status && (
                                <div className="sms-inbox-detail-meta">
                                    Status: <span className="badge bg-secondary">{selected.Status}</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="sms-inbox-empty">
                            <i className="bi bi-chat-left-text"></i>
                            <p>Select a message to read</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
